import type { CSSProperties, Key, ReactNode } from "react";
import { cn } from "../../lib/cn";
import { TABLE_BODY_MAX_H } from "../../lib/useStickyOffset";
import { Skeleton } from "./Skeleton";

export interface TableColumn<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  width?: string;
  align?: "left" | "right" | "center";
  /**
   * Hides the column below `md`. The table still scrolls horizontally, so this is for columns
   * that are genuinely secondary — never for the one carrying the row's identity.
   */
  hideBelowMd?: boolean;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  getRowId: (row: T) => Key;
  onRowPress?: (row: T) => void;
  emptyState?: ReactNode;
  loading?: boolean;
  loadingRowCount?: number;
  /** Freezes the first column while the rest scrolls under it on narrow screens. */
  stickyFirstColumn?: boolean;
  /** Pins the column headers below the page title and filter toolbar. */
  stickyHeader?: boolean;
  /**
   * Opt-in row selection. Omitting these leaves the table byte-for-byte as it was — no extra
   * column, no behaviour change — which is why every existing caller needed no edit.
   */
  selectedIds?: Set<Key>;
  onSelectionChange?: (next: Set<Key>) => void;
  className?: string;
}

const alignClasses = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
} as const;

/**
 * The one table in the app. Messaging, Templates, Contacts, Devices, API Keys and the dashboard
 * all render through this so a row looks identical wherever you meet it.
 *
 * Narrow screens get horizontal scroll with an optionally frozen first column — chosen over a
 * stacked-card layout because these tables carry scannable, comparable values (status, battery,
 * timestamps) that stop being comparable once each row becomes its own card. One approach is
 * applied everywhere rather than mixing the two.
 */
export function Table<T>({
  columns,
  data,
  getRowId,
  onRowPress,
  emptyState,
  loading = false,
  loadingRowCount = 5,
  stickyFirstColumn = false,
  stickyHeader = false,
  selectedIds,
  onSelectionChange,
  className,
}: TableProps<T>) {
  if (!loading && data.length === 0) {
    return <>{emptyState}</>;
  }

  const selectable = Boolean(selectedIds && onSelectionChange);
  const pageIds = data.map(getRowId);
  const selectedOnPage = pageIds.filter((id) => selectedIds?.has(id)).length;
  const allOnPageSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;

  function toggleAll() {
    if (!selectedIds || !onSelectionChange) return;
    const next = new Set(selectedIds);
    // Scoped to the CURRENT page: "select all" on page 1 must not silently arm a delete for
    // rows the user has never seen.
    if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    onSelectionChange(next);
  }

  function toggleOne(id: Key) {
    if (!selectedIds || !onSelectionChange) return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  }

  // Frozen cells need an opaque background of their own, or the scrolling columns show through.
  const stickyCell = (index: number, base: string) =>
    stickyFirstColumn && index === 0
      ? cn("sticky left-0 z-[1]", base, "after:absolute after:inset-y-0 after:-right-px after:w-px after:bg-border")
      : undefined;

  return (
    <div className={cn("overflow-hidden rounded-card border border-border bg-surface", className)}>
      {/*
        `overflow-x-auto` makes this element a scroll container on BOTH axes — the spec turns the
        other axis from visible into auto. That is why a viewport-relative offset on the <thead>
        does nothing: a sticky descendant anchors to its nearest scrollport, which is this div,
        not the page.

        So rather than fight it, this div IS the table body's scrollport when stickyHeader is on:
        it gets a height budget and the header sticks to `top-0` within it — which is exactly the
        "only the table body scrolls" structure, and is correct by construction rather than by
        keeping three measured offsets in agreement.

        The budget is only applied from `sm` upward. At 360px the viewport is ~640px tall and the
        title plus a wrapped filter row already claim over half of it; constraining further would
        leave a ~4-row window that is worse to use than simply letting the page scroll. Below
        `sm` the header still sticks — to the top of an unconstrained container, which is its
        natural position, so nothing moves and nothing breaks.
      */}
      <div
        // Custom properties are not in React CSSProperties, hence the cast. Passing the calc
        // through a variable rather than maxHeight directly keeps the sm: breakpoint in CSS,
        // where it can respond to width without a JS media query.
        style={
          stickyHeader
            ? ({ "--sb-table-max-h": TABLE_BODY_MAX_H } as CSSProperties)
            : undefined
        }
        className={cn(
          "scrollbar-slim overflow-x-auto",
          stickyHeader && "sm:max-h-[var(--sb-table-max-h)] sm:overflow-y-auto",
        )}
      >
        <table className="w-full border-collapse text-sm">
          <thead className={cn(stickyHeader && "sticky top-0 z-[2]")}>
            <tr className="border-b border-border">
              {selectable && (
                <th scope="col" className="w-10 bg-surface px-3 py-3">
                  <input
                    type="checkbox"
                    aria-label={allOnPageSelected ? "Deselect all rows" : "Select all rows"}
                    checked={allOnPageSelected}
                    // Some-but-not-all reads as a dash rather than a tick, so the header never
                    // claims a state the rows do not have.
                    ref={(node) => {
                      if (node) node.indeterminate = selectedOnPage > 0 && !allOnPageSelected;
                    }}
                    onChange={toggleAll}
                    className="size-4 accent-accent"
                  />
                </th>
              )}
              {columns.map((col, index) => (
                <th
                  key={col.key}
                  scope="col"
                  style={{ width: col.width }}
                  className={cn(
                    // Header type is deliberately a different register from body text — smaller,
                    // heavier, letterspaced — so hierarchy survives without a heavy fill or rule.
                    "whitespace-nowrap bg-surface px-4 py-3 text-xs font-semibold uppercase tracking-wider text-text-muted",
                    alignClasses[col.align ?? "left"],
                    col.hideBelowMd && "hidden md:table-cell",
                    stickyCell(index, "bg-surface"),
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {loading
              ? Array.from({ length: loadingRowCount }).map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {selectable && <td className="w-10 px-3 py-3.5" />}
                    {columns.map((col, index) => (
                      <td
                        key={col.key}
                        className={cn(
                          "px-4 py-3.5",
                          col.hideBelowMd && "hidden md:table-cell",
                          stickyCell(index, "bg-surface"),
                        )}
                      >
                        {/* Staggered widths so a loading table reads as content, not as a grid of bars. */}
                        <Skeleton
                          variant="text"
                          className={["w-3/4", "w-1/2", "w-2/3", "w-5/6"][(rowIndex + index) % 4]}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              : data.map((row) => (
                  <tr
                    key={getRowId(row)}
                    onClick={onRowPress ? () => onRowPress(row) : undefined}
                    className={cn(
                      "group/row transition-colors duration-fast ease-standard",
                      "hover:bg-surface-hover/60",
                      onRowPress &&
                        "cursor-pointer focus-within:bg-surface-hover/60 active:bg-surface-hover",
                      selectedIds?.has(getRowId(row)) && "bg-accent-soft/40",
                    )}
                  >
                    {selectable && (
                      <td className="w-10 px-3 py-3.5">
                        <input
                          type="checkbox"
                          aria-label="Select row"
                          checked={selectedIds?.has(getRowId(row)) ?? false}
                          onChange={() => toggleOne(getRowId(row))}
                          // Without this a click would also fire onRowPress and open the row.
                          onClick={(e) => e.stopPropagation()}
                          className="size-4 accent-accent"
                        />
                      </td>
                    )}
                    {columns.map((col, index) => (
                      <td
                        key={col.key}
                        className={cn(
                          "px-4 py-3.5 align-middle text-text",
                          alignClasses[col.align ?? "left"],
                          col.hideBelowMd && "hidden md:table-cell",
                          // Inherits the row's hover tint rather than repainting, so the frozen
                          // column highlights with its row instead of staying flat.
                          stickyCell(index, "bg-surface group-hover/row:bg-surface-hover"),
                        )}
                      >
                        {col.render
                          ? col.render(row)
                          : String((row as Record<string, unknown>)[col.key] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
